import React from 'react';
import { X, Printer, MapPin, Clock, CheckSquare } from 'lucide-react';

const PatientPathwaySlip = ({ 
    queueNumber, 
    patientName, 
    doctor,
    diagnosis,
    journeySteps,
    estimatedTotalTime,
    onClose 
}) => {
    
    const handlePrint = () => {
        window.print();
    };
    
    const formatQueueNumber = (num) => {
        return String(num).padStart(3, '0');
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                {/* Screen View - Not Printed */}
                <div className="print:hidden">
                    <div className="flex justify-between items-center p-4 border-b">
                        <h3 className="text-lg font-semibold text-gray-900">Patient Treatment Pathway</h3>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    
                    <div className="flex gap-3 p-4 border-b bg-gray-50">
                        <button
                            onClick={handlePrint}
                            className="flex-1 bg-blue-500 text-white px-4 py-3 rounded-lg hover:bg-blue-600 flex items-center justify-center gap-2 font-medium"
                        >
                            <Printer className="w-5 h-5" />
                            Print Pathway Slip
                        </button>
                        <button
                            onClick={onClose}
                            className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                            Close
                        </button>
                    </div>
                </div>

                {/* Printable Content */}
                <div className="p-8 print:p-4">
                    {/* Header */}
                    <div className="text-center border-b-2 border-gray-300 pb-4 mb-6">
                        <h1 className="text-2xl font-bold text-blue-600 print:text-black">TREATMENT PATHWAY</h1>
                        <p className="text-sm text-gray-600">LIPAY AFYA HOSPITAL</p>
                    </div>

                    {/* Patient Info */}
                    <div className="mb-6 pb-4 border-b">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-xs text-gray-600">Queue Number:</p>
                                <p className="text-2xl font-bold text-blue-600 print:text-black">#{formatQueueNumber(queueNumber)}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-600">Patient Name:</p>
                                <p className="text-lg font-semibold text-gray-900">{patientName}</p>
                            </div>
                        </div>
                        {doctor && (
                            <div className="mt-3">
                                <p className="text-xs text-gray-600">Physician:</p>
                                <p className="font-medium text-gray-900">{doctor}</p>
                            </div>
                        )}
                        {diagnosis && (
                            <div className="mt-3">
                                <p className="text-xs text-gray-600">Diagnosis:</p>
                                <p className="font-medium text-gray-900">{diagnosis}</p>
                            </div>
                        )}
                    </div>

                    {/* Important Instructions */}
                    <div className="mb-6 p-4 bg-yellow-50 border-2 border-yellow-300 rounded-lg print:border-black">
                        <p className="font-bold text-gray-900 mb-2">📋 IMPORTANT INSTRUCTIONS:</p>
                        <ul className="text-sm text-gray-700 space-y-1">
                            <li>• Keep this slip with you at all times</li>
                            <li>• Show it at each location</li>
                            <li>• Wait for your queue number to be called</li>
                            <li>• Check off each step as you complete it</li>
                        </ul>
                    </div>

                    {/* Journey Steps */}
                    <div className="mb-6">
                        <h3 className="font-bold text-gray-900 mb-4 text-lg">YOUR JOURNEY:</h3>
                        <div className="space-y-4">
                            {journeySteps && journeySteps.map((step, index) => (
                                <div key={index} className="border-2 border-gray-300 rounded-lg p-4 print:border-black print:break-inside-avoid">
                                    <div className="flex items-start gap-3">
                                        {/* Checkbox */}
                                        <div className="flex-shrink-0 mt-1">
                                            <div className="w-6 h-6 border-2 border-gray-400 rounded print:border-black" />
                                        </div>
                                        
                                        <div className="flex-1">
                                            {/* Step Header */}
                                            <div className="flex items-center justify-between mb-2">
                                                <p className="font-bold text-gray-900">
                                                    STEP {index + 1}: {step.label || step.locationType?.toUpperCase()}
                                                </p>
                                                {step.priority === 'urgent' && (
                                                    <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded text-xs font-semibold print:border print:border-black">
                                                        URGENT
                                                    </span>
                                                )}
                                            </div>

                                            {/* Location Details */}
                                            <div className="mb-2">
                                                <div className="flex items-center gap-2">
                                                    <MapPin className="w-4 h-4 text-gray-500" />
                                                    <p className="font-semibold text-gray-900">{step.roomName || step.nextRoom}</p>
                                                </div>
                                                {step.fullAddress && (
                                                    <p className="text-sm text-gray-600 ml-6">{step.fullAddress}</p>
                                                )}
                                            </div>

                                            {/* Step Details */}
                                            {step.testType && (
                                                <p className="text-sm text-gray-700 ml-6 mb-1">
                                                    <span className="font-medium">Tests:</span> {step.testType}
                                                </p>
                                            )}
                                            {step.instructions && (
                                                <p className="text-sm text-gray-700 ml-6 mb-1">
                                                    <span className="font-medium">Instructions:</span> {step.instructions}
                                                </p>
                                            )}
                                            {step.requiresReturn && (
                                                <p className="text-sm text-green-600 ml-6 font-medium">
                                                    → After this, return to consultation room
                                                </p>
                                            )}

                                            {/* Wait Instructions */}
                                            <div className="mt-3 ml-6 text-sm bg-blue-50 p-2 rounded border border-blue-200 print:bg-white print:border-black">
                                                <p className="font-medium text-gray-900">What to do:</p>
                                                <p className="text-gray-700 text-xs mt-1">
                                                    1. Go to waiting area<br />
                                                    2. Wait for "Queue #{formatQueueNumber(queueNumber)}" on display<br />
                                                    3. Proceed to room when called
                                                </p>
                                            </div>

                                            {/* Estimated Time */}
                                            {step.estimatedDuration && (
                                                <div className="flex items-center gap-2 mt-2 ml-6 text-xs text-gray-500">
                                                    <Clock className="w-3 h-3" />
                                                    Estimated: ~{step.estimatedDuration} minutes
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Total Time */}
                    {estimatedTotalTime && (
                        <div className="mb-6 p-4 bg-gray-100 rounded-lg border">
                            <div className="flex items-center gap-2">
                                <Clock className="w-5 h-5 text-gray-600" />
                                <div>
                                    <p className="text-sm text-gray-600">Estimated Total Time:</p>
                                    <p className="text-lg font-bold text-gray-900">{estimatedTotalTime} minutes</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Footer */}
                    <div className="mt-6 pt-4 border-t text-center">
                        <p className="text-xs text-gray-500">
                            Issued: {new Date().toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                            Please keep this slip until your visit is complete
                        </p>
                        <p className="text-xs text-gray-400 mt-2">
                            If you have questions, show this slip to any staff member
                        </p>
                    </div>
                </div>

                {/* Print-specific styles */}
                <style jsx>{`
                    @media print {
                        @page {
                            margin: 0.5in;
                            size: letter;
                        }
                        body {
                            print-color-adjust: exact;
                            -webkit-print-color-adjust: exact;
                        }
                    }
                `}</style>
            </div>
        </div>
    );
};

export default PatientPathwaySlip;

